<template lang="pug">
div(:class="isMobileScreen ? 'pt-4 px-4 pb-4 mobile-screen' : 'pt-6 px-8 pb-8'")
  //- Header section
  .d-flex.align-center(:class="isMobileScreen ? 'justify-end' : 'justify-space-between'")
    .yz-heading-5.neutral700--text(v-if="!isMobileScreen") Inventory
    hq-location-filter(v-model="locationFilter" :class="isMobileScreen ? '' : 'px-5'")

  //- Content card
  yz-card.mt-4(:class="isMobileScreen ? 'px-3 py-3' : 'px-5 py-4'")
    //- Desktop: Tabs and actions row
    template(v-if="!isMobileScreen")
      .d-flex.justify-space-between.align-center
        yz-tabs(v-model="activeTab" type="line")
          yz-tab(label="All")
          yz-tab(label="In-stock")
          yz-tab(label="Reserved")
          yz-tab(label="Low stock")
          yz-tab(label="Out of stock")
          yz-tab(label="Expiring soon")
          yz-tab(label="Archived")
        .d-flex.align-center.gap-2
          yz-search-btn(v-model="searchText" size="lg" @search="onSearch" @toggle="isSearchExpanded = $event")
          template(v-if="!isSearchExpanded")
            customize-column-menu(type="hq_inventory" :show-tooltip="false" icon-size="21")
            v-menu(offset-y left rounded="lg")
              template(v-slot:activator="{ on }")
                div(v-on="on")
                  yz-btn(label="Price list" size="lg" outlined append-icon="$yzi-download-02" :loading="isDownloading")
              v-list(dense)
                v-list-item(@click="onDownload('pdf')")
                  v-list-item-content
                    v-list-item-title.text-sm.neutral700--text PDF
                v-list-item(@click="onDownload('csv')")
                  v-list-item-content
                    v-list-item-title.text-sm.neutral700--text CSV
            yz-btn(label="Add new" size="lg" @click="onAddNew")

    //- Mobile: Scrollable tabs and compact actions
    template(v-else)
      .d-flex.align-center.justify-space-between.mb-2
        yz-search-btn(v-model="searchText" size="lg" @search="onSearch")
        yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onAddNew")
      .overflow-x-auto
        .d-flex.align-center(style="width: max-content;")
          div(v-for="tab in tabItems" :key="tab.id")
            .px-3.py-2.cursor-pointer(@click="activeTab = tab.id")
              span.yz-medium-body-sm(:class="activeTab === tab.id ? 'neutral700--text' : 'neutral500--text'") {{ tab.label }}
            .bar-active(v-if="activeTab === tab.id")

    yz-divider.my-0

    //- Filter row
    inventory-filter-row(v-if="isInitialized" ref="filterRow" :filters.sync="filters" @reset="onResetFilters" @update:filters="onFiltersChange")

    //- Table
    inventory-table(v-if="isInitialized" ref="inventoryTable" :is-mobile="isMobileScreen" :active-tab="activeTab" :search-text="searchText" :filters="filters" :location-filter="locationFilter" @view-item="onViewItem" @edit-item="onEditItem" @archive-item="onArchiveItem" @request-quotation="onRequestQuotation" @create-po="onCreatePo" @stock-history="onStockHistory")

  add-new-medicine-dialog(v-if="showAddNewDialog" :open.sync="showAddNewDialog" :medication-id="editingMedicationId" @closed="onDialogClosed" @created="onMedicineCreated" @updated="onMedicineUpdated")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import InventoryFilterRow from './InventoryFilterRow.vue'
import InventoryTable from './InventoryTable.vue'
import AddNewMedicineDialog from '@/components/hq/inventory/branches-warehouses/dialog/AddNewMedicineDialog.vue'
import HqLocationFilter from './HqLocationFilter.vue'

export default {
  components: {
    CustomizeColumnMenu,
    InventoryFilterRow,
    InventoryTable,
    AddNewMedicineDialog,
    HqLocationFilter
  },
  data () {
    return {
      activeTab: 0,
      searchText: '',
      filters: [],
      locationFilter: null,
      isDownloading: false,
      showAddNewDialog: false,
      editingMedicationId: null,
      isSearchExpanded: false,
      isInitialized: false,
      tabNameToIndex: {
        all: 0,
        'in-stock': 1,
        reserved: 2,
        'low-stock': 3,
        'out-of-stock': 4,
        expired: 5,
        archived: 6
      },
      tabIndexToName: ['all', 'in-stock', 'reserved', 'low-stock', 'out-of-stock', 'expired', 'archived']
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    tabItems () {
      return [
        { id: 0, label: 'All' },
        { id: 1, label: 'In-stock' },
        { id: 2, label: 'Reserved' },
        { id: 3, label: 'Low stock' },
        { id: 4, label: 'Out of stock' },
        { id: 5, label: 'Expired' },
        { id: 6, label: 'Archived' }
      ]
    }
  },
  watch: {
    activeTab (newTab) {
      this.updateTabQuery(newTab)
    },
    '$route.query.tab': {
      handler (newTabName) {
        if (newTabName) {
          const tabIndex = this.tabNameToIndex[newTabName]
          if (tabIndex !== undefined && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      }
    },
    '$route.query.search': {
      handler (newSearch) {
        const searchValue = newSearch || ''
        if (this.searchText !== searchValue) {
          this.searchText = searchValue
        }
      }
    },
    '$route.query.filters': {
      handler (newFilters) {
        const decoded = this.deserializeFilters(newFilters)
        if (JSON.stringify(this.filters) !== JSON.stringify(decoded)) {
          this.filters = decoded
        }
      }
    }
  },
  created () {
    this.initializeStateFromRoute()
    this.isInitialized = true
  },
  methods: {
    ...mapActions(['showMessage']),
    initializeStateFromRoute () {
      const { tab, search, filters } = this.$route.query

      if (tab && this.tabNameToIndex[tab] !== undefined) {
        this.activeTab = this.tabNameToIndex[tab]
      } else {
        this.updateTabQuery(0)
      }

      if (search) {
        this.searchText = search
      }

      if (filters) {
        this.filters = this.deserializeFilters(filters)
      }
    },
    updateTabQuery (tabIndex) {
      const tabName = this.tabIndexToName[tabIndex]
      if (tabName && this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: tabName }
        }).catch(() => {})
      }
    },
    updateSearchQuery (search) {
      const newQuery = { ...this.$route.query }
      if (search) {
        newQuery.search = search
      } else {
        delete newQuery.search
      }
      if (this.$route.query.search !== newQuery.search) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    updateFiltersQuery (filters) {
      const newQuery = { ...this.$route.query }
      const serialized = this.serializeFilters(filters)
      if (serialized) {
        newQuery.filters = serialized
      } else {
        delete newQuery.filters
      }
      if (this.$route.query.filters !== newQuery.filters) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    serializeFilters (filters) {
      if (!filters || filters.length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    deserializeFilters (base64String) {
      if (!base64String) {
        return []
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        return []
      }
    },
    onSearch (value) {
      this.searchText = value
      this.updateSearchQuery(value)
      this.$refs.inventoryTable.currentPage = 1
      this.$refs.inventoryTable.selectedItems = []
      this.$refs.inventoryTable.fetchMedications()
    },
    onResetFilters () {
      this.filters = []
      this.updateFiltersQuery([])
    },
    onFiltersChange (newFilters) {
      this.filters = newFilters
      this.updateFiltersQuery(newFilters)
    },
    onAddNew () {
      this.editingMedicationId = null
      this.showAddNewDialog = true
    },
    onDialogClosed () {
      this.showAddNewDialog = false
      this.editingMedicationId = null
    },
    onMedicineCreated () {
      this.showAddNewDialog = false
      this.editingMedicationId = null
      this.$refs.inventoryTable.fetchMedications()
    },
    onMedicineUpdated () {
      this.showAddNewDialog = false
      this.editingMedicationId = null
      this.$refs.inventoryTable.fetchMedications()
    },
    onViewItem (item) {
      this.$emit('view-item', item)
    },
    onEditItem (item) {
      this.editingMedicationId = item.id
      this.showAddNewDialog = true
    },
    onArchiveItem (item) {
      this.$emit('archive-item', item)
    },
    onRequestQuotation (items) {
      this.$emit('request-quotation', items)
    },
    onCreatePo (items) {
      this.$emit('create-po', items)
    },
    onStockHistory (item) {
      this.$router.push({ name: 'HQStockHistory', params: { id: item.id } })
    },
    async onDownload (type) {
      this.isDownloading = true
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isDownloading = false
        return
      }
      let baseUrl
      if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      } else {
        baseUrl = 'https://export-test.yezza.io'
      }
      const url = new URL(`${baseUrl}/clinics/${this.company.slug}/medications/preview_document`)
      url.searchParams.set('key', key)
      const params = {
        status: 'published',
        export: type
      }
      if (this.activeTab === 1) { params.stock__gt = 0 }
      if (this.activeTab === 2) { params.reserved = true }
      if (this.activeTab === 3) { params.low_stock = true }
      if (this.activeTab === 4) { params.stock__lte = 0 }
      if (this.activeTab === 5) { params.expire_soon = true }
      if (this.activeTab === 6) { params.status = 'draft' }
      if (this.searchText) { params.search = this.searchText }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      if (this.locationFilter) { params.branches = this.locationFilter }
      for (const objKey in params) {
        url.searchParams.set(objKey, params[objKey])
      }
      window.open(url, '_blank')
      this.isDownloading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
  scrollbar-width: none;
}
.bar-active {
  height: 2px;
  width: 100%;
  margin: auto;
  margin-top: -2px;
  background-color: #333;
}
</style>
